import { Market } from "../Market";
import { observable, action, computed } from "mobx";
import { parseTimeFull, eBranches } from "sb-resp-lib";
import { TennisScore } from "..";
import { TennisScoreParser } from "../../parsers/TennisScoreParser";
/**
* MasterEvent defines model for master object information
*
* @typedef MasterEvent
* @type {Object}
* @property {id} BranchId identifier
* @property {team1Name} Name of the first team
* @property {team2Name} Name of the second team
* @property {leagueID} Id of the league
* @property {leagueName} Name of the league
* @property {masterEventDate} Date when event will start in requested format
* @property {liveTimeElapsed} Time in ms, that has passed form the event start
* @property {branchID} ID of the branch
* @property {branchName} Name of the branch
* @property {markets} List of markets for this event
* @property {danger} If event is in danger mode
* @property {isTeamSwapEnabled} If team swapping is enabled
* @property {isLive} If event is live or prelive
* @property {tennisData} Object containing tennis live event details
* @property {gameStatus} Status of the live event current timeframe
*/

export type MasterEventType = {
    id: string;
    team1Name: string;
    team2Name: string;
    score1: string;
    score2: string;
    leagueID: string;
    leagueName: string;
    eventDate: string;
    eventTime: string;
    liveTimeElapsed: number;
    branchID: string;
    branchName: string;
    markets: Market[];
    isTeamSwapEnabled: boolean;
    isLive: boolean;
    allMarketsCount: number;
    hasFastMarketsSpecials: boolean;
    hasLiveStream: boolean;
    danger: boolean;
    tennisData: TennisScore;
    betRadarStatisticsEventId?: number;
    homeEnetplusID?: number;
    awayEnetplusID?: number;
    selectionHandler?: Function;
    link: string;
    additionalData?: any;
    gameData: any;
    masterEventDate: Date;
    gameStatusProvider?: GameStatus;
    statisticsInfo?: any;
    statisticsInfoClickHandler?: Function;
}

export class MasterEvent {
    @observable eventDate: string;
    @observable eventTime: string;
    @observable liveTimeElapsed: number;
    id: string;
    team1Name: string;
    team2Name: string;
    @observable score1: string;
    @observable score2: string;
    leagueID: string;
    leagueName: string;
    branchID: string;
    branchName: string;
    @observable markets: Market[];
    isTeamSwapEnabled: boolean;
    isLive: boolean;
    @observable allMarketsCount: number;
    hasFastMarketsSpecials: boolean;
    hasLiveStream: boolean;
    danger: boolean;
    tennisData: TennisScore;
    betRadarStatisticsEventId?: number;
    homeEnetplusID?: number;
    awayEnetplusID?: number;
    selectionHandler?: Function;
    link: string;
    additionalData?: any;
    @observable gameData: any;
    masterEventDate: Date;
    gameStatusProvider?: GameStatus;
    statisticsInfo?: any;
    statisticsInfoClickHandler?: Function;

    constructor(masterEventData: MasterEventType) {
        this.id = masterEventData.id;
        this.team1Name = masterEventData.team1Name;
        this.team2Name = masterEventData.team2Name;
        this.score1 = masterEventData.score1;
        this.score2 = masterEventData.score2;
        this.leagueID = masterEventData.leagueID;
        this.leagueName = masterEventData.leagueName;
        this.eventDate = masterEventData.eventDate;
        this.eventTime = masterEventData.eventTime;
        this.liveTimeElapsed = masterEventData.liveTimeElapsed;
        this.branchID = masterEventData.branchID;
        this.branchName = masterEventData.branchName;
        this.isTeamSwapEnabled = masterEventData.isTeamSwapEnabled;
        this.isLive = masterEventData.isLive;
        this.markets = masterEventData.markets;
        this.allMarketsCount = masterEventData.allMarketsCount;
        this.hasFastMarketsSpecials = masterEventData.hasFastMarketsSpecials;
        this.hasLiveStream = masterEventData.hasLiveStream;
        this.danger = masterEventData.danger;
        this.tennisData = masterEventData.tennisData;
        this.betRadarStatisticsEventId = masterEventData.betRadarStatisticsEventId;
        this.homeEnetplusID = masterEventData.homeEnetplusID;
        this.awayEnetplusID = masterEventData.awayEnetplusID;
        masterEventData.selectionHandler && (this.selectionHandler = masterEventData.selectionHandler);
        this.link = masterEventData.link;
        this.additionalData = masterEventData.additionalData;
        this.gameData = masterEventData.gameData;
        this.masterEventDate = masterEventData.masterEventDate;
        this.gameStatusProvider = masterEventData.gameStatusProvider;
    }

    @action
    updateTimeDate(newTime: string, newDate: string) {
        this.eventTime = newTime;
        this.eventDate = newDate;
    }

    @action.bound updateTime(delta: number) {
        return this.liveTimeElapsed += delta;
    }

    @action.bound updateScore(score1: string, score2: string) {
        this.score1 = score1;
        this.score2 = score2;
    }

    @computed get timeTicking(): string {
        if (this.liveTimeElapsed === 0) { return ""; }

        return parseTimeFull(this.liveTimeElapsed);
    }
    /* tslint:disable:cyclomatic-complexity */
    //Got this method from Mobile (modified)
    @computed get gameStatus(): string {
        const config = {
            additionalData: this.additionalData,
            isShortStatusText: true
        };
        let statusText = "";
        const branchID = parseInt(this.branchID);

        if (this.gameData &&
            this.gameStatusProvider &&
            !this.isSoccerEventFinished() && // Don't show 'Full Time' if soccer event is over
            this.isEnabledForLive() && (true || this.isStarted())) // sometimes we want the status even before the event starts - ex. just before kick-off
        {
            if (this.isExtraTimeEnabledForEventExceptBasketball(this.gameData, branchID)) {
                //statusText = $string("GameStatusShort").ET;
                statusText = this.gameStatusProvider.jsonShortStatusCodes.parts.overtime;
            }
            // NOTE: If we need statuses for all sports we have to replace it
            // with `else` statement only
            else if (branchID === eBranches.Soccer ||
                branchID === eBranches.Basketball ||
                branchID === eBranches.IceHockey) {
                let returnedText = this.gameStatusProvider.getStatusController(branchID, this.gameData.Status, false, config);
                statusText = returnedText === null ? "" : returnedText;
            }
        }

        return statusText;
    }
    /* tslint:enable:cyclomatic-complexity */

    isSoccerEventFinished(): boolean {
        return this.gameData.Status === 6 && this.branchID === eBranches.Soccer.toString();
    }

    isEnabledForLive(): boolean {
        //to-do: implement for show live 5 minutes before Kickoff time
        // var now = getDataTime(timeControl.getServerTime());
        // // show live 5 minutes before Kickoff time
        // var d = new Date(getDataTime(this.MasterEventDate));
        // d.setMinutes(d.getMinutes() - StartLiveBettingInMinutes);
        // return d.getTime() < now.getTime();
        return this.isLive;
    }

    isStarted(): boolean {
        //to-do: implement this for KickOff time
        // var now = getDataTime(timeControl.getServerTime());
        // var mEvDate = getDataTime(this.MasterEventDate);
        // return mEvDate.getTime() < now.getTime();
        return this.isLive;
    }

    isExtraTimeEnabledForEventExceptBasketball(gameData, branchID): boolean {
        //Basketball overtime is checked by the game status which is 60 when the Overtime part is started
        return gameData.IsET === 1 && branchID !== eBranches.Basketball.toString();
    }

    @computed get updatedTennisData(): TennisScore {
        return TennisScoreParser.parseData(this.score1, this.score2);
    }

    @action.bound updateLiveTimeElapsed(liveTimeElapsed: number) {
        this.liveTimeElapsed = liveTimeElapsed;
    }
}